/**
 * panel-telemetry — авто-телеметрия пользовательской панели (Mini App).
 *
 * Один механизм на ВСЮ панель (не ручная разметка каждого экрана — хрупко, П0):
 *  - глобальный делегированный click-listener (capture) → метка через deriveClickLabel;
 *  - трекинг экранов по react-router pathname: screen_view на вход, screen_time на уход.
 * Постит на `/v1/panel/telemetry` (mobile-gw panelAuth → content-engine визор-лог).
 *
 * Почему useLocation, а не popstate: панель — SPA на react-router; переход по вкладке
 * (Link/navigate) делает history.pushState, который НЕ генерирует popstate (тот — только
 * back/forward). Слушать popstate → пропустить почти всю навигацию. useLocation
 * ре-рендерит на КАЖДУЮ смену маршрута → эффект ловит все переходы (П21 — через фреймворк).
 *
 * Антихрупкость (П5): fire-and-forget, `keepalive` (доживает закрытие Mini App),
 * `catch→no-op` — телеметрия НИКОГДА не блокирует и не ломает UX.
 * Приватность: deriveClickLabel берёт метку/текст, не `value` → данные форм не пишутся.
 * enabled=false (admin-preview) → полный no-op.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { authHeaders, type PanelAuth } from '../../lib/panel-auth';
import { deriveClickLabel } from './derive-click-label';

function post(apiBase: string, token: PanelAuth, body: Record<string, unknown>): void {
  try {
    void fetch(`${apiBase}/v1/panel/telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* телеметрия не критична (П5) */
  }
}

export interface PanelTelemetryOpts {
  apiBase: string;
  token: PanelAuth;
  enabled: boolean;
}

/** Глобальный авто-захват кликов + screen_view/time по маршруту. enabled=false → no-op. */
export function usePanelTelemetry(opts: PanelTelemetryOpts): void {
  const ref = useRef(opts);
  ref.current = opts;
  const { pathname } = useLocation();

  // Клики — делегирование на document в capture-фазе (ловит и будущие элементы).
  useEffect(() => {
    if (!opts.enabled) return;
    const onClick = (e: MouseEvent): void => {
      const label = deriveClickLabel(e.target as Element | null);
      if (!label) return;
      post(ref.current.apiBase, ref.current.token, { type: 'ux_click', target: label });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [opts.enabled]);

  // Экран: эффект перезапускается на КАЖДУЮ смену pathname (react-router ре-рендер).
  // Вход экрана → screen_view; уход (cleanup на смене маршрута/размонтировании) или
  // закрытие Mini App (pagehide) → screen_time с временем на экране.
  useEffect(() => {
    if (!opts.enabled) return;
    const screen = pathname;
    const start = Date.now();
    post(ref.current.apiBase, ref.current.token, { type: 'screen_view', screen });
    const emitTime = (): void =>
      post(ref.current.apiBase, ref.current.token, {
        type: 'screen_time',
        screen,
        seconds: Math.round((Date.now() - start) / 1000),
      });
    window.addEventListener('pagehide', emitTime);
    return () => {
      emitTime();
      window.removeEventListener('pagehide', emitTime);
    };
  }, [opts.enabled, pathname]);
}
